'use client';

import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  IconButton,
  MetaDefault,
  MonoDefault,
  toasterApi,
  useThemeMode,
} from '@terra/ui-design-system';
import {
  Box,
  Center,
  Container,
  HStack,
  VStack,
} from '@terra/ui-styled-system';
import { Copy01Icon, Logout01Icon } from '@terra/ui-icons';
import {
  IP_ALLOWLIST_DENIED_EVENT,
  readIpAllowlistDeniedIp,
} from '@terra/shared-utils';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useAuth } from '../app/providers/auth/useAuth';

type DeniedState = { ip: string | null };

export function IpAllowlistDeniedGate() {
  const [denied, setDenied] = useState<DeniedState | null>(null);
  const { logout, isAuthenticated } = useAuth();
  const { mode: themeMode } = useThemeMode();
  const t = useTranslations('ipAllowlist');
  const tLayout = useTranslations('layout');
  const tSignIn = useTranslations('signIn');
  const tCommon = useTranslations('common');

  useEffect(() => {
    if (!isAuthenticated) {
      setDenied(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const onDenied = (event: Event) =>
      setDenied({ ip: readIpAllowlistDeniedIp(event) });

    window.addEventListener(IP_ALLOWLIST_DENIED_EVENT, onDenied);

    return () => {
      window.removeEventListener(IP_ALLOWLIST_DENIED_EVENT, onDenied);
    };
  }, []);

  if (!denied) {
    return null;
  }

  const deniedIp = denied.ip;

  const copyIp = async (ip: string) => {
    try {
      if (window.electronAPI?.clipboard?.writeText) {
        const result = await window.electronAPI.clipboard.writeText(ip);

        if (!result.success) {
          throw new Error(
            result.error ?? tLayout('credentialsMenu.copyFailed'),
          );
        }
      } else {
        await navigator.clipboard.writeText(ip);
      }

      toasterApi.success({ title: tLayout('credentialsMenu.copied') });
    } catch (error) {
      toasterApi.error({
        title: tLayout('credentialsMenu.copyFailed'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <Box
      position="fixed"
      inset="0"
      zIndex={10000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="bg.canvas"
      overflow="hidden"
    >
      <Container maxWidth="md" maxH="100%" overflowY="auto" py={4}>
        <Card.Root maxW="md" w="full">
          <Card.Header>
            <Center>
              <VStack gap={3}>
                <Image
                  src={
                    themeMode === 'dark'
                      ? '/images/icon-light.svg'
                      : '/images/icon-dark.svg'
                  }
                  alt={tSignIn('logoAlt')}
                  width={64}
                  height={64}
                  priority
                />
                <Card.Title textAlign="center">{t('blockedTitle')}</Card.Title>
                <Card.Description textAlign="center">
                  {t('blockedDescription')}
                </Card.Description>
              </VStack>
            </Center>
          </Card.Header>
          <Card.Body>
            <VStack gap={4}>
              <Box
                w="full"
                p={3}
                borderRadius="md"
                bg="bg.subtle"
                borderWidth="1px"
                borderColor="border.subtle"
              >
                {deniedIp ? (
                  <HStack gap={3} alignItems="center" justify="space-between">
                    <VStack gap={0.5} alignItems="flex-start" minW={0}>
                      <MetaDefault color="fg.muted">
                        {t('blockedIpLabel')}
                      </MetaDefault>
                      <MonoDefault color="fg.default" wordBreak="break-all">
                        {deniedIp}
                      </MonoDefault>
                    </VStack>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={tCommon('copy')}
                      onClick={() => {
                        void copyIp(deniedIp);
                      }}
                    >
                      <Copy01Icon size={14} />
                    </IconButton>
                  </HStack>
                ) : (
                  <MetaDefault color="fg.muted">
                    {t('blockedIpUnknown')}
                  </MetaDefault>
                )}
              </Box>
              <Button
                variant="outline"
                size="md"
                w="full"
                onClick={() => {
                  setDenied(null);
                  void logout();
                }}
              >
                <Logout01Icon size={20} />
                {tLayout('logout')}
              </Button>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
}
